import { rhythmAllowsManyDays, RHYTHMS } from '@/config/dropWizardSteps'
import { formatClock } from '@/helpers/dropWizardDates'
import { mergeDraft } from '@/helpers/wizardDraft'

export { rhythmAllowsManyDays, RHYTHMS, WEEKDAYS } from '@/config/dropWizardSteps'

export const WIZARD_PATH = '/store/fulfillment-methods/setup'

export const WIZARD_STEPS = [
  { key: 'method', index: 0, label: 'Method' },
  { key: 'where', index: 1, label: 'Where' },
  { key: 'stores', index: 2, label: 'Stores' },
  { key: 'days', index: 3, label: 'Days' },
  { key: 'times', index: 4, label: 'Time' },
  { key: 'ordering', index: 5, label: 'Ordering' },
  { key: 'pricing', index: 6, label: 'Cost' },
  { key: 'review', index: 7, label: 'Review' }
]

export const QUESTION_STEPS = WIZARD_STEPS.length - 1

export const stepForKey = key => WIZARD_STEPS.find(s => s.key === key)

export const METHOD_TYPES = [
  {
    value: 'pickup',
    label: 'Pickup',
    subtitle: 'Customers collect from you',
    apiType: 'PickupFulfillmentMethod',
    icon: 'store'
  },
  {
    value: 'delivery',
    label: 'Delivery',
    subtitle: 'You bring orders to customers',
    apiType: 'DeliveryFulfillmentMethod',
    icon: 'truck'
  },
  {
    value: 'shipping',
    label: 'Shipping',
    subtitle: 'You send orders with a carrier',
    apiType: 'ShippingFulfillmentMethod',
    icon: 'box'
  },
  {
    value: 'uber_direct',
    label: 'Uber Direct',
    subtitle: 'An Uber courier collects from you and delivers nearby',
    apiType: 'UberDirectFulfillmentMethod',
    icon: 'pin'
  }
]

export const DEFAULT_NAMES = {
  pickup: 'Pickup',
  delivery: 'Local delivery',
  shipping: 'Shipping',
  uber_direct: 'Uber Direct delivery'
}

export const ADDRESS_TYPES = ['pickup', 'uber_direct']
export const ZONE_TYPES = ['delivery', 'shipping']

export const ZONE_KINDS = [
  { value: 'map', label: 'Draw the area on a map', recommended: true },
  { value: 'zips', label: 'Zip codes' },
  { value: 'states', label: 'States' }
]

export const SHIPPING_AREAS = [
  { value: 'anywhere', label: country => `Anywhere in ${country}` },
  { value: 'states', label: () => 'Certain states' },
  { value: 'zips', label: () => 'Certain zip codes' }
]

export const methodLabel = data => data.name.trim() || 'this method'

export const RECOMMENDED_RHYTHM = 'weekly'

export const DAYS_SUBTITLE = 'Bottle keeps six months of dates ahead and adds more as time passes.'
export const ONE_DAY_NOTE = 'Pick one day. Every other week and every 4 weeks repeat on one day.'
export const ONCE_DATE_DAYS = 42
export const LATER_DATE_LABELS = { open: 'Pick a later date', close: 'Show the next 6 weeks' }

export const ARRIVAL_PRESETS = [
  { value: 'morning', label: 'Morning', start: '08:00', end: '12:00' },
  { value: 'midday', label: 'Midday', start: '11:00', end: '15:00' },
  { value: 'afternoon', label: 'Afternoon', start: '12:00', end: '17:00' },
  { value: 'all_day', label: 'All day', start: '08:00', end: '17:00' },
  { value: 'evening', label: 'Evening', start: '16:00', end: '20:00' },
  { value: 'custom', label: 'Custom', start: null, end: null }
]

export const PRODUCTION_OPTIONS = [
  { value: 0, name: 'Same day' },
  { value: 1, name: 'The day before' },
  { value: 'earlier', name: 'Earlier' }
]

export const MAX_PRODUCTION_DAYS_BEFORE = 7

const QUARTERS_PER_DAY = 96

export const QUARTER_HOURS = Array.from({ length: QUARTERS_PER_DAY }, (_, i) => {
  const value = `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`
  return { value, name: formatClock(value) }
})

export const CUTOFF_PRESETS = [
  {
    value: 'morning_of',
    label: 'The morning of, 6:00 AM',
    subtitle: 'Last-minute orders right up to production',
    daysBefore: 0,
    time: '06:00'
  },
  {
    value: 'day_before',
    label: 'The day before, 8:00 PM',
    subtitle: 'The most common choice. Gives you a night to plan',
    daysBefore: 1,
    time: '20:00',
    recommended: true
  },
  {
    value: 'two_days',
    label: 'Two days before, 12:00 PM',
    subtitle: 'Time to order ingredients before you start',
    daysBefore: 2,
    time: '12:00'
  },
  { value: 'custom', label: 'Custom', subtitle: 'Pick the day and time', daysBefore: null, time: null }
]

export const CUTOFF_NOTE = 'Counted from the production day, the day you make the orders.'

export const MAX_CUTOFF_DAYS_BEFORE = 7

export const LIMIT_OPTIONS = [
  { value: false, name: 'No limit' },
  { value: true, name: 'Set a limit' }
]

export const MINIMUM_OPTIONS = [
  { value: false, name: 'No minimum' },
  { value: true, name: 'Set a minimum' }
]

export const OPENS_OPTIONS = [
  { value: 'previous', name: 'When the previous one closes', recommended: true, days: null },
  { value: 'always', name: 'Right away', days: null },
  { value: 'week_before', name: 'A week before', days: 7 },
  { value: 'three_days', name: '3 days before', days: 3 }
]

export const DROP_CHARGE_NOTE = 'Drops that auto-charge run at the same moment.'
export const PRODUCTION_NOTE = 'This is the production day. Product availability and drops follow this day.'
export const DROP_HINT_LABEL = 'What is a drop?'
export const DROP_EXPLAINER =
  'A drop is a scheduled ordering round for a store. Bottle reminds customers to order for the drop, and a drop set to auto-charge charges their saved carts the moment ordering closes.'

export const DROP_HINT_END = 'This creates no drop. Set one up from Drops.'

export const NEXT_DATES_LABELS = {
  pickup: 'Next pickup dates',
  delivery: 'Next delivery dates',
  shipping: 'Next ship dates',
  uber_direct: 'Next courier dates'
}
export const NEXT_DATES_HINT = 'The first days customers get their order, from the days and time you picked.'

export const PRICING_COPY = {
  pickup: {
    heading: 'Is there a pickup fee?',
    subtitle: () => 'Most pickup is free. If you charge one, customers see the fee at checkout as a Fulfillment Fee.',
    feeLabel: 'Pickup fee per order'
  },
  delivery: {
    heading: 'What does delivery cost the customer?',
    subtitle: name =>
      `Customers see the fee next to ${name} and on their order as a Delivery Fee. Free delivery shows 'Free'.`,
    feeLabel: 'Delivery fee per order'
  },
  shipping: {
    heading: 'What does shipping cost the customer?',
    subtitle: name =>
      `Customers see the fee next to ${name} and on their order as a Shipping Fee. Free shipping shows 'Free'.`,
    feeLabel: 'Shipping fee per order'
  },
  uber_direct: {
    heading: 'Do you charge on top of the courier fee?',
    subtitle: () =>
      "Uber's courier fee is quoted live at checkout and added to the delivery line. Anything you enter here is charged on top of the courier fee.",
    feeLabel: 'Your fee per order'
  }
}

export const DAYS_HEADINGS = {
  pickup: 'Which days can customers pick up their orders?',
  delivery: 'Which days do you deliver orders?',
  shipping: 'Which days do orders ship?',
  uber_direct: 'Which days does the courier deliver orders?'
}

export const TIME_HEADINGS = {
  pickup: 'What time can customers pick up their order?',
  delivery: 'What time do you deliver?',
  shipping: 'When do orders go out?',
  uber_direct: 'What time does the courier collect?'
}

export const WHERE_HEADINGS = {
  pickup: 'Where do customers pick up?',
  delivery: 'Where do you deliver?',
  shipping: 'Where do you ship?',
  uber_direct: 'Where does the courier collect from?'
}

const ZONE_SUBTITLE =
  'Customers enter their address at checkout. We only offer this method when the address is inside the area.'

const WHERE_SUBTITLES = {
  pickup: 'Customers see this address at checkout and on the pickup map.',
  uber_direct: 'Customers never see this address. The courier collects here.',
  delivery: ZONE_SUBTITLE,
  shipping: ZONE_SUBTITLE
}

const WINDOW_TITLES = {
  pickup: 'Pickup window',
  delivery: 'Delivery window',
  shipping: 'Shipping window',
  uber_direct: 'Courier window'
}

const pricingCopy = data => PRICING_COPY[data.type] ?? PRICING_COPY.pickup

const HEADINGS = {
  method: () => 'How will customers get their order?',
  where: data => WHERE_HEADINGS[data.type],
  stores: data => `Which stores offer ${methodLabel(data)}?`,
  days: data => DAYS_HEADINGS[data.type],
  times: data => TIME_HEADINGS[data.type],
  ordering: () => 'When should ordering close?',
  pricing: data => pricingCopy(data).heading,
  review: () => 'Does this look right?'
}

export const stepHeading = (key, data) => HEADINGS[key]?.(data) ?? ''

const SUBTITLES = {
  method: () => 'This sets what customers see at checkout and what we ask you next.',
  where: data => WHERE_SUBTITLES[data.type],
  stores: data =>
    `Customers only see ${methodLabel(data)} on the stores you pick. You can change this later on the store's Checkout tab.`,
  days: () => DAYS_SUBTITLE,
  times: () => 'Customers see this window at checkout and on their receipt.',
  ordering: () => 'Customers can order until the cutoff.',
  pricing: data => pricingCopy(data).subtitle(methodLabel(data)),
  review: data => `Everything here can be changed from the page for ${methodLabel(data)} afterwards.`
}

export const stepSubtitle = (key, data) => SUBTITLES[key]?.(data) ?? ''

const SECTIONS = {
  method: () => ({ title: 'Method', description: 'How customers receive their orders.' }),
  name: () => ({ title: 'Name customers see', description: 'Customers see this name next to the method at checkout.' }),
  address: data =>
    data.type === 'uber_direct'
      ? { title: 'Courier pickup point', description: 'Where the courier collects orders from you.' }
      : { title: 'Pickup address', description: 'Where customers come to collect their orders.' },
  zone: data =>
    data.type === 'shipping'
      ? { title: 'Shipping area', description: 'Ship anywhere, or only to certain states or zip codes.' }
      : { title: 'Delivery area', description: 'Draw the area on a map, or list zip codes or states.' },
  stores: data => ({ title: 'Stores', description: `Switch on each store that offers ${methodLabel(data)}.` }),
  rhythm: () => ({ title: 'How often', description: 'The days below repeat on this rhythm.' }),
  days: () => ({ title: 'Days', description: 'The days of the week the dates fall on.' }),
  date: data => ({ title: 'Date', description: `The one date for ${methodLabel(data)}.` }),
  window: data => ({
    title: WINDOW_TITLES[data.type] ?? WINDOW_TITLES.pickup,
    description: 'Pick a preset, or Custom to set your own times.'
  }),
  production: () => ({
    title: 'Production day',
    description: 'The day you make the orders, counted back from the day customers receive their orders.'
  }),
  closes: () => ({ title: 'Ordering closes', description: CUTOFF_NOTE }),
  opens: () => ({ title: 'Ordering opens', description: 'When customers can start ordering for the next date.' }),
  limit: () => ({ title: 'Order limit', description: 'The most orders you take for each date.' }),
  fee: data => ({
    title: pricingCopy(data).feeLabel,
    description: `Added to every order with ${methodLabel(data)}.`
  }),
  minimum: () => ({ title: 'Minimum order', description: 'The smallest order total customers can check out with.' })
}

export const sectionCopy = (key, data) => SECTIONS[key](data)

export const defaultWizardData = () => ({
  type: null,
  name: '',
  nameEdited: false,
  address: { address1: '', address2: '', city: '', state: null, zip: '' },
  zone: { kind: 'map', waypoints: [], zipCodes: [], states: [] },
  storeIds: [],
  schedule: {
    rhythm: 'weekly',
    weekdays: [],
    date: null,
    arrival: { preset: 'all_day', start: '08:00', end: '17:00' },
    productionDaysBefore: 0,
    closes: { preset: 'day_before', daysBefore: 1, time: '20:00' },
    opens: { kind: 'previous', days: 7 },
    limit: null
  },
  pricing: { priceCents: 0, taxable: false, minimumOrderCents: null }
})

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const validTime = t => typeof t === 'string' && TIME.test(t)
const filled = s => typeof s === 'string' && s.trim().length > 0
const nonNegative = n => Number.isFinite(n) && n >= 0
const weekday = n => Number.isInteger(n) && n >= 0 && n <= 6

const zoneValid = zone => {
  if (zone.kind === 'map') return zone.waypoints.length >= 3
  if (zone.kind === 'zips') return zone.zipCodes.length > 0
  if (zone.kind === 'states') return zone.states.length > 0
  return zone.kind === 'anywhere'
}

const addressValid = a => filled(a.address1) && filled(a.city) && filled(a.state) && filled(a.zip)

const VALIDATORS = {
  method: d => !!METHOD_TYPES.find(t => t.value === d.type) && filled(d.name),
  where: d => (ADDRESS_TYPES.includes(d.type) ? addressValid(d.address) : zoneValid(d.zone)),
  stores: d => d.storeIds.length > 0,
  days: d => {
    const { rhythm, weekdays, date } = d.schedule
    if (!RHYTHMS.some(r => r.value === rhythm)) return false
    if (rhythm === 'once') return filled(date)
    if (!weekdays.length || !weekdays.every(weekday)) return false
    return rhythmAllowsManyDays(rhythm) || weekdays.length === 1
  },
  times: d =>
    validTime(d.schedule.arrival.start) &&
    validTime(d.schedule.arrival.end) &&
    d.schedule.arrival.end > d.schedule.arrival.start &&
    Number.isInteger(d.schedule.productionDaysBefore) &&
    d.schedule.productionDaysBefore >= 0 &&
    d.schedule.productionDaysBefore <= MAX_PRODUCTION_DAYS_BEFORE,
  ordering: d => {
    const { closes, limit, arrival, productionDaysBefore } = d.schedule
    if (
      !validTime(closes.time) ||
      !Number.isInteger(closes.daysBefore) ||
      closes.daysBefore < 0 ||
      closes.daysBefore > MAX_CUTOFF_DAYS_BEFORE
    )
      return false
    if (productionDaysBefore === 0 && closes.daysBefore === 0 && closes.time >= arrival.start) return false
    return limit === null || (Number.isInteger(limit) && limit > 0)
  },
  pricing: d =>
    nonNegative(d.pricing.priceCents) &&
    (d.pricing.minimumOrderCents === null || nonNegative(d.pricing.minimumOrderCents)),
  review: () => true
}

export const stepValid = (key, data) => (VALIDATORS[key] ? VALIDATORS[key](data) : false)

export const allStepsValid = data => WIZARD_STEPS.filter(s => s.key !== 'review').every(s => stepValid(s.key, data))

const ZONE_KINDS_BY_TYPE = {
  delivery: ZONE_KINDS.map(k => k.value),
  shipping: SHIPPING_AREAS.map(a => a.value)
}
const OPENS_KINDS = ['previous', 'always', 'days_before']
const OPENS_DAYS = OPENS_OPTIONS.map(o => o.days).filter(Boolean)
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const known = (value, values) => values.includes(value)
const wholeIn = (n, min, max) => Number.isInteger(n) && n >= min && n <= max
const point = p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite)
const texts = list => list.filter(item => typeof item === 'string')

const restoreZone = (zone, type, fallback) => {
  const kinds = ZONE_KINDS_BY_TYPE[type] ?? [fallback.kind]
  zone.kind = known(zone.kind, kinds) ? zone.kind : type === 'shipping' ? 'anywhere' : fallback.kind
  zone.waypoints = zone.waypoints.filter(point)
  zone.zipCodes = texts(zone.zipCodes)
  zone.states = texts(zone.states)
}

const restorePreset = (current, presets, fallback, fields) => {
  const preset = presets.find(p => p.value === current.preset)
  if (!preset) return { ...fallback }
  if (preset.value === 'custom') return current
  return { ...current, ...Object.fromEntries(fields.map(([key, from]) => [key, preset[from]])) }
}

const restoreOpens = (opens, rhythm, fallback) => {
  if (!known(opens.kind, OPENS_KINDS)) return { ...fallback }
  if (opens.kind === 'days_before' && !known(opens.days, OPENS_DAYS)) return { ...fallback }
  if (opens.kind === 'previous' && rhythm === 'once') return { kind: 'always', days: null }
  return opens
}

const restoreSchedule = (schedule, fallback) => {
  schedule.rhythm = RHYTHMS.some(r => r.value === schedule.rhythm) ? schedule.rhythm : fallback.rhythm
  const weekdays = [...new Set(schedule.weekdays.filter(weekday))]
  schedule.weekdays = rhythmAllowsManyDays(schedule.rhythm) ? weekdays : weekdays.slice(0, 1)
  schedule.date = typeof schedule.date === 'string' && ISO_DATE.test(schedule.date) ? schedule.date : null
  schedule.arrival = restorePreset(schedule.arrival, ARRIVAL_PRESETS, fallback.arrival, [
    ['start', 'start'],
    ['end', 'end']
  ])
  if (!wholeIn(schedule.productionDaysBefore, 0, MAX_PRODUCTION_DAYS_BEFORE))
    schedule.productionDaysBefore = fallback.productionDaysBefore
  schedule.closes = restorePreset(schedule.closes, CUTOFF_PRESETS, fallback.closes, [
    ['daysBefore', 'daysBefore'],
    ['time', 'time']
  ])
  schedule.opens = restoreOpens(schedule.opens, schedule.rhythm, fallback.opens)
  if (schedule.limit !== null && !wholeIn(schedule.limit, 1, Number.MAX_SAFE_INTEGER)) schedule.limit = null
}

const restorePricing = pricing => {
  if (!nonNegative(pricing.priceCents)) pricing.priceCents = 0
  if (pricing.minimumOrderCents !== null && !nonNegative(pricing.minimumOrderCents)) pricing.minimumOrderCents = null
}

export const restoreWizardData = saved => {
  const fallback = defaultWizardData()
  const data = mergeDraft(defaultWizardData(), saved, { sameType: true })
  if (!METHOD_TYPES.some(t => t.value === data.type)) data.type = null
  if (data.address.state !== null && typeof data.address.state !== 'string') data.address.state = null
  restoreZone(data.zone, data.type, fallback.zone)
  data.storeIds = data.storeIds.filter(id => ['string', 'number'].includes(typeof id)).map(String)
  restoreSchedule(data.schedule, fallback.schedule)
  restorePricing(data.pricing)
  return data
}
